import React, { useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Menu, Brain } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Check screen size and set initial sidebar state
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768; // md breakpoint
      setIsMobile(mobile);
      setSidebarCollapsed(mobile);
      // Close mobile sidebar when switching to desktop
      if (!mobile) {
        setMobileSidebarOpen(false);
      }
    };

    // Set initial state
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 dark:from-gray-950 dark:via-blue-950 dark:to-indigo-950">
      {/* Mobile sidebar overlay */}
      {isMobile && mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeMobileSidebar}
        />
      )}
      
      <Sidebar
        isCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        isOpen={mobileSidebarOpen}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        onMobileClose={closeMobileSidebar}
      />
      
      {/* Hamburger menu and logo for mobile */}
      {isMobile && (
        <div className="fixed top-4 left-4 z-30 flex items-center space-x-4">
          <button
            onClick={toggleMobileSidebar}
            className="p-2 rounded-lg bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-lg hover:bg-white dark:hover:bg-gray-900 transition-colors duration-200"
            aria-label="Otwórz menu"
          >
            <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          </button>
          
          <div className="flex items-center space-x-2">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl blur-lg opacity-30 animate-pulse-glow"></div>
              <div className="relative bg-gradient-to-r from-primary-500 to-secondary-500 p-2 rounded-xl">
                <Brain className="w-5 h-5 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-bold gradient-text">CallOS</h1>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                AI Sales Analyzer
              </p>
            </div>
          </div>
        </div>
      )}
      
      <main className={`transition-all duration-300 ease-in-out ${
        isMobile
          ? 'ml-0'
          : sidebarCollapsed
            ? 'ml-20'
            : 'ml-64'
      }`}>
        <div className={`container mx-auto px-4 py-6 ${isMobile ? 'pt-16' : ''}`}>
          {children}
        </div>
      </main>
    </div>
  );
};